/**
 * The undo stack behind the 10 s toast (spec M1-B4 §8.4).
 *
 * Per session, in memory, max 50 entries. Two kinds of entry live on the same stack, in the
 * order they happened (P-050):
 *
 *  - `cells` — a cell edit, a `Delete` on a range, a paste. Its inverse is a batch of writes,
 *    and it is invalidated for a cell whose remote value changed since it was recorded, so
 *    "Undo" can never resurrect a value someone else replaced.
 *  - `restore` / `unarchive` — a row delete or archive. Its inverse is a restore of **exactly**
 *    the items that action removed, matched on the server's `action_record_uuid` (`[B3 D6]`);
 *    the service puts each row back in its own group and position (D3 relocates only when the
 *    group itself is gone).
 *
 * Undo re-issues the inverse through the service with `is_undo_action: true` `[ba§2]`.
 *
 * **P-105 — redo.** The stack is bidirectional: `undoLast` moves the entry it just inverted
 * onto the redo stack, `redoLast` re-applies its `applied` half the same way, and any *new*
 * mutation clears the redo stack (the standard rule). A redo goes through the service as a
 * fresh mutation — `is_undo_action: false` — because that is what it is. monday's own redo is
 * not captured (**q498**); session-scoped, both bindings, cleared by a new mutation is the
 * conservative reading.
 */
import type { ColumnId, ItemId } from '@elizmonday/shared';
import type { CellWrite } from '../data/types';

export const UNDO_LIMIT = 50;
export const UNDO_TOAST_MS = 10_000;

interface UndoEntryBase {
  readonly id: string;
  readonly label: string;
  readonly at: number;
}

export interface CellsUndoEntry extends UndoEntryBase {
  readonly kind: 'cells';
  /** The writes that put the board back the way it was. */
  readonly inverse: readonly CellWrite[];
  /** What the entry itself wrote — used to detect a remote overwrite. */
  readonly applied: readonly CellWrite[];
  /**
   * **P-326.** A **subitem** cell lives on the linked subitems board, so the inverse names that
   * board (an undo sent to the parent board would be refused for a row it does not hold) and
   * the parent whose nested table repaints afterwards. Absent for an item cell.
   */
  readonly boardId?: string;
  readonly parentItemId?: string;
}

/**
 * A row delete (`restore`) or archive (`unarchive`). `actionRecordUuid` is the handle the
 * one-call undo uses; a source that has none (the in-memory one) falls back to the ids.
 */
export interface ItemsUndoEntry extends UndoEntryBase {
  readonly kind: 'restore' | 'unarchive';
  readonly itemIds: readonly ItemId[];
  readonly actionRecordUuid: string | null;
  /**
   * **P-304.** The board the rows live on. A **subitem** lives on the board's linked subitems
   * board, not on the board the grid is showing, so the inverse call has to name that board
   * too — an undo sent to the parent board matched no rows and put nothing back, which is the
   * same silence the delete itself had.
   */
  readonly boardId?: string;
  /** The parent whose nested table and count bubble the undo has to repaint (P-304). */
  readonly parentItemId?: string;
}

/**
 * **P-087.** A deleted column. `columns.remove` is a *soft* delete — the row and its
 * `cell_values` are kept so the column and every value in it come back — so the inverse is
 * `columns.restore` on the same id and the redo is the delete again. It is a third kind on
 * the same stack rather than a special case inside `cells`: the inverse is one call, not a
 * batch of cell writes, and `Ctrl/Cmd+Z` must be able to reach it in order like any other.
 */
export interface ColumnUndoEntry extends UndoEntryBase {
  readonly kind: 'column';
  readonly columnId: ColumnId;
}

export type UndoEntry = CellsUndoEntry | ItemsUndoEntry | ColumnUndoEntry;

export interface UndoStack {
  readonly entries: readonly UndoEntry[];
  /** The entries an undo has taken off `entries`, newest first (P-105). */
  readonly redo: readonly UndoEntry[];
}

export const EMPTY_UNDO_STACK: UndoStack = { entries: [], redo: [] };

/** A **new** mutation invalidates every redo — the branch it would replay is gone (P-105). */
export function pushUndo(stack: UndoStack, entry: UndoEntry): UndoStack {
  const entries = [entry, ...stack.entries].slice(0, UNDO_LIMIT);
  return { entries, redo: [] };
}

export function peekUndo(stack: UndoStack): UndoEntry | null {
  return stack.entries[0] ?? null;
}

export function popUndo(stack: UndoStack): UndoStack {
  return { entries: stack.entries.slice(1), redo: stack.redo };
}

/**
 * The entry an undo inverted, moved onto the redo stack. Called by `undoLast` **after** the
 * inverse has been accepted, so a refused undo leaves nothing to redo.
 */
export function pushRedo(stack: UndoStack, entry: UndoEntry): UndoStack {
  return { entries: stack.entries, redo: [entry, ...stack.redo].slice(0, UNDO_LIMIT) };
}

export function peekRedo(stack: UndoStack): UndoEntry | null {
  return stack.redo[0] ?? null;
}

/** Takes the top of the redo stack off and puts it back on the undo stack, where it was. */
export function popRedo(stack: UndoStack, entry: UndoEntry): UndoStack {
  return {
    entries: [entry, ...stack.entries].slice(0, UNDO_LIMIT),
    redo: stack.redo.filter((candidate) => candidate.id !== entry.id),
  };
}

/** A redo that the service refused: the entry goes back on the redo stack, unchanged (E8). */
export function restoreRedo(stack: UndoStack, entry: UndoEntry): UndoStack {
  return {
    entries: stack.entries.filter((candidate) => candidate.id !== entry.id),
    redo: [entry, ...stack.redo.filter((candidate) => candidate.id !== entry.id)].slice(0, UNDO_LIMIT),
  };
}

/**
 * The toast's **Undo** targets the entry that toast was raised for, never "the last thing that
 * happened": a cell edit landing while the delete toast is still up must not steal its button
 * (P-050). `Ctrl/Cmd+Z` has no such handle and keeps peeking at the top of the stack.
 */
export function findUndo(stack: UndoStack, id: string): UndoEntry | null {
  return stack.entries.find((entry) => entry.id === id) ?? null;
}

export function dropUndo(stack: UndoStack, id: string): UndoStack {
  return { entries: stack.entries.filter((entry) => entry.id !== id), redo: stack.redo };
}

function key(itemId: ItemId, columnId: ColumnId): string {
  return `${itemId} ${columnId}`;
}

/**
 * Drops from every entry the cells a later remote change touched, and drops entries left with
 * nothing. `changed` is the set of cells patched by `cell.changed` events that did not match
 * one of our own pending mutation ids (§8.12). A `restore` / `unarchive` entry owns no cells
 * and is never invalidated this way — the items it names are gone, so nobody can edit them.
 */
export function invalidate(stack: UndoStack, changed: ReadonlySet<string>): UndoStack {
  if (changed.size === 0) return stack;
  const prune = (list: readonly UndoEntry[]): UndoEntry[] => {
    const out: UndoEntry[] = [];
    for (const entry of list) {
      if (entry.kind !== 'cells') {
        out.push(entry);
        continue;
      }
      const inverse = entry.inverse.filter((write) => !changed.has(key(write.itemId, write.columnId)));
      if (inverse.length === 0) continue;
      out.push(inverse.length === entry.inverse.length ? entry : { ...entry, inverse });
    }
    return out;
  };
  // A cell somebody else changed is out of **both** stacks: a redo may no more resurrect a
  // value than an undo may.
  return { entries: prune(stack.entries), redo: prune(stack.redo) };
}

export function cellKey(itemId: ItemId, columnId: ColumnId): string {
  return key(itemId, columnId);
}

/** The toast wording for a batch of cleared cells (§8.3 `Delete`). */
export function clearLabel(count: number): string {
  return count === 1 ? 'Cleared 1 cell' : `Cleared ${count} cells`;
}

/** `Deleted 1 item` / `Deleted 3 items` — monday pluralises the noun (**ASSUMED**, Q158). */
export function deleteLabel(count: number): string {
  return count === 1 ? 'Deleted 1 item' : `Deleted ${count} items`;
}

export function archiveLabel(count: number): string {
  return count === 1 ? 'Archived 1 item' : `Archived ${count} items`;
}

/**
 * The toast for a cell edit that a **row selection** spread over several items (P-263):
 * monday's own wording is *"N items updated"*, counted in **items**, not cells — one column is
 * written, so the two counts are the same number, but the noun is monday's.
 */
export function massEditLabel(count: number): string {
  return count === 1 ? '1 item updated' : `${count} items updated`;
}
